import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from '../../../authService';
import DashboardHome from '../../DashboardHome';
import CustomersPage from '../../pages/CustomersPage';
import BillingPage from '../../pages/BillingPage';
import NoticePage from '../../pages/NoticePage';
import PaymentsPage from '../../pages/PaymentsPage';
import OnlinePaymentsPage from '../../pages/OnlinePaymentsPage';
import CreditManagementPage from '../../pages/CreditManagementPage';
import ReportsPage from '../../pages/ReportsPage';
import UsageAnomalyPage from '../../pages/UsageAnomalyPage';
import ReadingSheetsPage from '../../pages/ReadingSheetsPage';
import CustomerLogbookPage from '../../pages/CustomerLogbookPage';
import WaterBillingSettingsPage from '../../pages/WaterBillingSettingsPage';
import ProfileModal from '../../modals/ProfileModal';
import SettingsModal from '../../modals/SettingsModal';
import MobileBottomBar from '../../common/MobileBottomBar';
import UnifiedSidebar from '../../common/UnifiedSidebar';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Receipt,
  Payment,
  Savings,
  Assessment,
  ManageAccounts,
  ArrowBack,
  WaterDrop,
  Settings,
  Logout,
  Person,
  Description,
  Warning,
  MenuBook
} from '@mui/icons-material';

const drawerWidth = 280;

const WaterBillingDashboard = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const currentUser = authService.getUserSession();
    if (!currentUser) {
      navigate('/login');
    } else {
      setUser(currentUser);
    }
    document.title = 'Water Billing System - LGU Concepcion';
  }, [navigate]);

  // Redirect to dashboard home if on exact path
  useEffect(() => {
    if (location.pathname === '/portal/water-billing') {
      navigate('/portal/water-billing/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Listen for navigation to payments event from BillModal
  useEffect(() => {
    const handleNavigateToPayments = (event) => {
      navigate('/portal/water-billing/payments');
    };

    window.addEventListener('navigateToPayments', handleNavigateToPayments);
    return () => window.removeEventListener('navigateToPayments', handleNavigateToPayments);
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    authService.logout();
    if (onLogout) {
      onLogout();
    }
    navigate('/login', { replace: true });
  };

  const handleProfileClick = () => {
    handleMenuClose();
    setShowProfileModal(true);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    setShowSettingsModal(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    authService.setUserSession(updatedUser);
  };

  const isSuperAdmin = String(user?.role || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '') === 'superadmin';

  const menuItems = [
    { name: 'Dashboard', icon: <Dashboard />, path: '/portal/water-billing/dashboard' },
    { name: 'Customers', icon: <People />, path: '/portal/water-billing/customers' },
    { name: 'Customer Logbook', icon: <MenuBook />, path: '/portal/water-billing/customer-logbook' },
    { name: 'Billing', icon: <Receipt />, path: '/portal/water-billing/billing' },
    { name: 'Payments', icon: <Payment />, path: '/portal/water-billing/payments' },
    { name: 'Notices', icon: <Warning />, path: '/portal/water-billing/notices' },
    { name: 'Online Payments', icon: <Payment />, path: '/portal/water-billing/online-payments' },
    { name: 'Credits', icon: <Savings />, path: '/portal/water-billing/credit-management' },
    { name: 'Reports', icon: <Assessment />, path: '/portal/water-billing/reports' },
    { name: 'Usage Anomalies', icon: <Assessment />, path: '/portal/water-billing/usage-anomalies' },
    { name: 'Reading Sheets', icon: <Description />, path: '/portal/water-billing/reading-sheets' },
    ...(isSuperAdmin ? [{ name: 'Settings', icon: <Settings />, path: '/portal/water-billing/settings' }] : [])
  ];

  const drawer = (
    <UnifiedSidebar
      user={user}
      onLogout={handleLogout}
      onCloseMobile={() => setMobileOpen(false)}
      onOpenProfile={handleProfileClick}
      onOpenSettings={handleSettingsClick}
    />
  );

  if (!user) return null;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Mobile Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          display: { md: 'none' },
          bgcolor: '#ffffff',
          color: '#0f172a',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, color: '#0f172a' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WaterDrop sx={{ color: '#059669', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
              Water Billing System
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side Navigation Drawers */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          pb: { xs: 10, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: '#f8faf9',
          minHeight: '100vh',
          mt: { xs: 7, md: 0 }
        }}
      >
        <Routes>
          <Route index element={<DashboardHome user={user} />} />
          <Route path="dashboard" element={<DashboardHome user={user} />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customer-logbook" element={<CustomerLogbookPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="notices" element={<NoticePage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="online-payments" element={<OnlinePaymentsPage />} />
          <Route path="credit-management" element={<CreditManagementPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="usage-anomalies" element={<UsageAnomalyPage />} />
          <Route path="reading-sheets" element={<ReadingSheetsPage />} />
          <Route
            path="settings"
            element={isSuperAdmin ? <WaterBillingSettingsPage /> : <Navigate to="/portal/water-billing/dashboard" replace />}
          />
        </Routes>
      </Box>

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={menuItems}
        currentPath={location.pathname}
        onNavigate={(path) => navigate(path)}
        onToggleMenu={handleDrawerToggle}
        isMenuOpen={mobileOpen}
      />

      {/* Modals */}
      <ProfileModal
        user={user}
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onUpdate={handleProfileUpdate}
      />
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </Box>
  );
};

export default WaterBillingDashboard;
